import router from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { AuthState, useAuth } from '@app/hooks/useAuth';
import { useFirebase } from '@app/hooks/useFirebase';
import { clampToString, cn, timeAgo } from '@app/libs/utils';
import { notificationsService } from '@app/services/notifications.service';

import Avatar from '../dashboard/Avatar';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import BellIcon from '../icons/BellIcon';
import CheckIcon from '../icons/CheckIcon';
import PlusIcon from '../icons/PlusIcon';
import StarIcon from '../icons/StarIcon';

import { Button } from './Button';
import Tooltip from './Tooltip';

interface NotificationBadgeProps {
  align?: 'left' | 'right';
  top?: number;
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({ align = 'right', top = 88 }) => {
  const { isAuth } = useAuth();
  const { notifications: notiCount, permission, requestPermission } = useFirebase();
  const [count, setCount] = useState<string>('');
  const [show, setShow] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [status, setStatus] = useState<'unread' | 'read' | 'all'>('unread');
  const [ignoreRequestPermission, setIgnoreRequestPermission] = useState<boolean>(false);
  const [listNotification, setListNotification] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [totalItem, setTotalItem] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchNotifications = async (pi: number = 1, pageSize: number = 20) => {
    setIsLoading(true);
    try {
      const { success, data, errors, totalItem } = await notificationsService.getNotifications(
        status,
        pi,
        pageSize
      );
      if (!success || errors) throw errors;

      setTotalItem(totalItem);
      if (pi === 1) {
        setListNotification(data);
      } else {
        setListNotification(prev => [...prev, ...data]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    const { success, errors } = await notificationsService.readAllNotifications();
    setListNotification(prev =>
      prev.map(notification => ({ ...notification, readStatus: 'READ' }))
    );
  };

  const handleMarkAsRead = async (notification: any) => {
    await notificationsService.readNotification(notification.id);
    if (notification.module && notification.objectId) {
      switch (notification.module) {
        case 'TRANSACTION':
          router.push(`/dashboard/transactions/${notification.objectId}`);
          handleToggle();
          break;
        case 'NEWS':
          router.push(`/dashboard/listing/${notification.objectId}`);
          handleToggle();
          break;
        case 'USER':
          router.push(`/dashboard/profile`);
          handleToggle();
          break;
      }
    }
    setListNotification(prev =>
      prev.map(notification =>
        notification.id === notification.id ? { ...notification, readStatus: 'READ' } : notification
      )
    );
  };

  useEffect(() => {
    if (sessionStorage.getItem('ignoreRequestPermission') === 'true') {
      setIgnoreRequestPermission(true);
    }
  }, []);

  useEffect(() => {
    if (isAuth !== AuthState.AUTH) return;

    fetchNotifications(1, 20);
  }, [status, isAuth]);

  useEffect(() => {
    sessionStorage.setItem('ignoreRequestPermission', ignoreRequestPermission.toString());
  }, [ignoreRequestPermission]);

  useEffect(() => {
    setCount(clampToString(notiCount, 0, 99));
  }, [notiCount]);

  const handleToggle = () => {
    if (show) {
      setIsClosing(true);
      setTimeout(() => {
        setShow(false);
        setIsClosing(false);
      }, 300); // Đợi animation slide-out hoàn thành
    } else {
      setShow(true);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
    }, 300);
  };

  const getUser = (notification: any) => {
    const username = notification.fullName ?? notification.updatedBy;
    if (!username || username === 'system') return 'Hệ thống';

    return notification.createdByName ?? notification.updatedByName ?? username;
  };

  if (isAuth !== AuthState.AUTH) return null;

  return (
    <>
      <div className="relative" onClick={handleToggle}>
        <BellIcon
          size={24}
          className="text-gray-400 hover:text-blue-800 transition-colors duration-200 hover-shake cursor-pointer"
        />
        {count != '0' && !!count && (
          <span className="absolute top-0 right-0 px-1 bg-red-500 rounded-full flex items-center justify-center text-white text-[8px]">
            {count}
          </span>
        )}
      </div>
      {show && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/20 z-40" onClick={handleClose} />
          {/* Panel */}
          <div
            className={cn(
              'fixed w-[calc(100vw-50px)] md:w-90 right-5 bottom-5 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-40 flex flex-col gap-4',
              isClosing
                ? align === 'left'
                  ? 'slide-out-left'
                  : 'slide-out-right'
                : align === 'left'
                  ? 'slide-in-left'
                  : 'slide-in-right',
              align === 'left' ? 'left-5' : 'right-5'
            )}
            style={{ top: `${top}px` }}
          >
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-700">Thông báo</span>
              <Button variant="none" className="text-[10px] h-5 w-5 !p-0" onClick={handleClose}>
                <PlusIcon className="w-5 h-5 rotate-45" />
              </Button>
            </div>
            {!ignoreRequestPermission && permission !== 'granted' && (
              <div className="flex flex-col justify-between items-center bg-gray-100 rounded-lg p-4 gap-4 ">
                <div className="text-sm text-gray-700 flex items-start">
                  Bạn đang tắt thông báo đẩy.
                  <br /> Bạn có thể bật lại bằng cách nhấn vào nút bên dưới.
                  <Button
                    variant="none"
                    className="text-[10px] h-4 w-4 !p-0"
                    tooltip="Tạm thời tắt thông báo đẩy"
                    onClick={() => setIgnoreRequestPermission(true)}
                  >
                    <PlusIcon className="w-4 h-4 rotate-45" />
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button variant="primary" className="text-[10px] h-8" onClick={requestPermission}>
                    Bật thông báo đẩy
                  </Button>
                  <Button
                    variant="secondary"
                    outline
                    className="text-[10px] h-8"
                    onClick={() => setIgnoreRequestPermission(true)}
                  >
                    Bỏ qua
                  </Button>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant={status === 'unread' ? 'secondary' : 'white'}
                className={cn(
                  'w-full text-xs hover:bg-gray-100',
                  status === 'unread' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                )}
                onClick={() => setStatus('unread')}
                disabled={isLoading}
              >
                Chưa đọc
              </Button>

              <Button
                variant={status === 'all' ? 'secondary' : 'white'}
                className={cn(
                  'w-full text-xs hover:bg-gray-100',
                  status === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                )}
                onClick={() => setStatus('all')}
                disabled={isLoading}
              >
                Tất cả
              </Button>
            </div>
            <Button
              variant="none"
              className="w-full text-xs text-blue-700 hover:text-gray-700 justify-start -my-4 !p-0 hover:underline gap-1"
              onClick={handleMarkAllAsRead}
              loadingText="Đang đánh dấu tất cả đã đọc..."
            >
              <CheckIcon size={12} className="shrink-0" />
              Đánh dấu tất cả đã đọc
            </Button>
            <div className="grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <div className="flex flex-col">
                {listNotification.map((notification, index) => (
                  <div
                    key={notification.id}
                    className={cn(
                      'flex items-center justify-between py-1 border-gray-50',
                      'transition-background-color duration-300',
                      index !== listNotification.length - 1 ? 'border-b' : ''
                    )}
                  >
                    <div
                      className={cn(
                        'flex flex-col items-start gap-2 w-full text-sm overflow-hidden p-2 rounded-sm',
                        'hover:bg-gray-100 cursor-pointer',
                        notification.readStatus === 'UNREAD' ? 'bg-yellow-50' : 'bg-white'
                      )}
                    >
                      {notification?.title && (
                        <span className="text-xs font-bold">{notification.title}</span>
                      )}
                      <div className="overflow-hidden flex justify-between items-start flex-col gap-2">
                        <input
                          type="checkbox"
                          id={`notification-${notification.id}`}
                          className="w-4 h-4 peer hidden"
                        />
                        <span
                          onClick={() => handleMarkAsRead(notification)}
                          className="overflow-hidden text-truncate text-ellipsis line-clamp-2 text-xs peer-checked:line-clamp-none"
                        >
                          {notification.content}
                        </span>
                        {notification.isPriority === 1 && (
                          <Tooltip content="Đánh dấu là quan trọng">
                            <StarIcon size={16} className="text-yellow-500 shrink-0" />
                          </Tooltip>
                        )}
                        <label
                          htmlFor={`notification-${notification.id}`}
                          className="ml-auto flex text-xs text-blue-700 hover:text-gray-700 !p-0 hover:underline gap-1 justify-end group peer-checked:!hidden"
                        >
                          <ArrowDownIcon size={12} className="shrink-0" />
                          Xem thêm
                        </label>
                        <label
                          htmlFor={`notification-${notification.id}`}
                          className="ml-auto hidden text-xs text-blue-700 hover:text-gray-700 !p-0 hover:underline gap-1 justify-end group peer-checked:flex"
                        >
                          <ArrowDownIcon size={12} className="shrink-0 rotate-180" />
                          Ẩn bớt
                        </label>
                      </div>
                      <div className="text-[10px] text-gray-500 text-right w-full justify-between flex gap-4 overflow-hidden">
                        <div className="flex items-center gap-1 overflow-hidden">
                          <Avatar
                            userInfo={{ ...notification, photoUrl: '/images/logo.svg' }}
                            width={16}
                            height={16}
                            className="min-w-4 h-4 rounded-full text-[10px] bg-gray-200 overflow-hidden"
                          />
                          <span className="block shrink-1 overflow-hidden text-truncate text-ellipsis line-clamp-1">
                            {notification.createdBy ?? getUser(notification)}
                          </span>
                        </div>
                        <span className="shrink-0">
                          {timeAgo(notification?.createdAt ?? notification?.updatedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {totalItem > listNotification.length && (
              <Button
                variant="none"
                className="w-full text-xs text-blue-700 hover:text-gray-700 -mb-4 hover:underline"
                loadingText="Đang tải dữ liệu..."
                onClick={() => fetchNotifications(pageIndex + 1, 20)}
              >
                Xem thông báo trước đó
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default NotificationBadge;
